import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SCOPE_LABEL } from '@dru/types';
import type { PermissionDomain, PermissionEntry, RoleRow, ScopeType } from '@dru/types';
import { ShieldCheck, TriangleAlert, X } from 'lucide-react';
import { Button, EmptyState, ErrorState, SearchInput, Select, TableSkeleton, PhoneFold, cn } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { NotForYourRole } from './Audit';

/**
 * The permission catalogue, read-only.
 *
 * Read-only on purpose. A permission is not configuration — it is a name that
 * appears in a `@RequirePermission` decorator in the API, and inventing one
 * here would produce a permission that gates nothing while looking as though
 * it gates something. That is worse than not having it: somebody grants it,
 * believes an action is restricted, and it never was.
 *
 * So the screen answers the question people actually arrive with, which is
 * not "what permissions exist" but **"who can do this?"** — every entry names
 * the roles that hold it. Roles are edited next door.
 */
export function Permissions() {
  const { permissions } = useAuth();
  if (!(permissions.has('user.role-assign'))) {
    return <NotForYourRole title="Permissions" what="The permission catalogue is read by platform administrators." />;
  }
  return <PermissionsPage />;
}

/** `appointment.noshow` → "Noshow"; `report.verify-cardiology` → "Verify cardiology". */
const WORDS: Record<string, string> = { checkin: 'check in', noshow: 'no-show', checkout: 'check out' };
function actionLabel(key: string): string {
  const action = key.split('.').slice(1).join(' ').replace(/[-_.]/g, ' ').trim()
    .split(' ').map((w) => WORDS[w] ?? w).join(' ') || key;
  return action.charAt(0).toUpperCase() + action.slice(1);
}
const domainLabel = (d: string) => (d.charAt(0).toUpperCase() + d.slice(1)).replace(/[-_]/g, ' ');

type Only = 'stepUp' | 'orphan' | null;

function PermissionsPage() {
  const catalogue = useQuery<PermissionDomain[]>(() => api.get('/admin/permissions'), []);
  // Names for the role keys. The same permission guards both reads.
  const roles = useQuery<RoleRow[]>(() => api.get('/admin/roles'), []);
  const [search, setSearch] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [only, setOnly] = useState<Only>(null);

  const roleByKey = useMemo(() => new Map((roles.data ?? []).map((r) => [r.key, r])), [roles.data]);
  const roleName = (key: string) => roleByKey.get(key)?.name ?? key;
  const selectedRole = roleKey ? roleByKey.get(roleKey) : undefined;

  const all = catalogue.data ?? [];
  const total = all.reduce((n, d) => n + d.permissions.length, 0);
  const stepUp = all.reduce((n, d) => n + d.permissions.filter((p) => p.stepUp).length, 0);
  const orphans = all.reduce((n, d) => n + d.permissions.filter((p) => p.heldBy.length === 0).length, 0);

  const domains = useMemo(() => {
    const term = search.trim().toLowerCase();
    const keep = (p: PermissionEntry) => {
      if (only === 'stepUp' && !p.stepUp) return false;
      if (only === 'orphan' && p.heldBy.length > 0) return false;
      if (roleKey && !p.heldBy.includes(roleKey)) return false;
      if (!term) return true;
      return p.key.toLowerCase().includes(term)
        || actionLabel(p.key).toLowerCase().includes(term)
        || p.heldBy.some((r) => r.toLowerCase().includes(term) || roleName(r).toLowerCase().includes(term));
    };
    return all
      .map((d) => ({ ...d, permissions: d.permissions.filter(keep) }))
      .filter((d) => d.permissions.length > 0);
    // roleName reads roleByKey, which is in the list.
  }, [all, search, roleKey, only, roleByKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = domains.reduce((n, d) => n + d.permissions.length, 0);
  const filtering = Boolean(search.trim() || roleKey || only);
  const clear = () => { setSearch(''); setRoleKey(''); setOnly(null); };

  // Roles grouped by where they apply, in the catalogue's own words.
  const roleGroups = useMemo(() => {
    const by = new Map<ScopeType, RoleRow[]>();
    for (const r of roles.data ?? []) by.set(r.scopeType, [...(by.get(r.scopeType) ?? []), r]);
    return [...by.entries()].map(([scope, rs]) => ({ scope, roles: rs.sort((a, b) => a.name.localeCompare(b.name)) }));
  }, [roles.data]);

  if (catalogue.error) {
    return <ErrorState detail={catalogue.error} onRetry={catalogue.reload} />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Permissions"
        hint={'Every permission the API enforces, and the roles that hold it. Read-only: a permission is a check in the '
          + 'API, so it cannot be made up here. Change what a role holds on the Roles page. Hiding a button changes what '
          + 'somebody sees, never what they can do.'}
        actions={<Link to="/admin/roles" className="text-small font-medium text-primary-600 hover:text-primary-700 max-md:inline-flex max-md:min-h-10 max-md:items-center">Edit roles →</Link>}
      />

      {/* What the catalogue holds, in one line; the two that need a look are filters. */}
      {!catalogue.loading && (
        <div className="flex flex-wrap items-center gap-2 text-small text-neutral-600">
          <span><strong className="font-semibold tabular-nums text-neutral-900">{total}</strong> permissions in{' '}
            <strong className="font-semibold tabular-nums text-neutral-900">{all.length}</strong> areas</span>
          <span aria-hidden className="text-neutral-300">·</span>
          <FilterChip active={only === 'stepUp'} onClick={() => setOnly(only === 'stepUp' ? null : 'stepUp')}
            icon={<ShieldCheck className="h-3.5 w-3.5" aria-hidden />}>
            {stepUp} need two-factor sign-in
          </FilterChip>
          {orphans > 0 && (
            <FilterChip tone="warning" active={only === 'orphan'} onClick={() => setOnly(only === 'orphan' ? null : 'orphan')}
              icon={<TriangleAlert className="h-3.5 w-3.5" aria-hidden />}>
              {orphans} held by no role
            </FilterChip>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search a permission or a role"
          aria-label="Search permissions or roles"
          className="w-full sm:w-80"
        />
        <label className="flex w-full flex-col gap-1 sm:w-72">
          <span className="text-caption font-medium text-neutral-700">What can this role do?</span>
          <Select value={roleKey} onChange={(e) => setRoleKey(e.target.value)} aria-label="Show the permissions one role holds">
            <option value="">Any role</option>
            {roleGroups.map((g) => (
              <optgroup key={g.scope} label={SCOPE_LABEL[g.scope] ?? g.scope}>
                {g.roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </optgroup>
            ))}
          </Select>
        </label>
        {filtering && (
          <Button variant="ghost" size="sm" icon={<X className="h-4 w-4" aria-hidden />} onClick={clear}>Clear filters</Button>
        )}
      </div>

      {selectedRole && !catalogue.loading && (
        <p className="text-small text-neutral-700" aria-live="polite">
          <strong className="font-semibold text-neutral-900">{selectedRole.name}</strong>
          <span className="font-mono text-caption text-neutral-500"> {selectedRole.key}</span>
          {' '}holds <strong className="tabular-nums">{selectedRole.permissionCount}</strong> of {total} permissions
          {' '}· {selectedRole.holders === 1 ? '1 person has' : `${selectedRole.holders} people have`} this role.
        </p>
      )}
      {filtering && !selectedRole && !catalogue.loading && shown > 0 && (
        <p className="text-small text-neutral-600" aria-live="polite">Showing {shown} of {total} permissions.</p>
      )}

      {catalogue.loading && (
        <div className="overflow-hidden rounded-card border border-neutral-200 bg-white">
          <TableSkeleton cols={3} />
        </div>
      )}

      {!catalogue.loading && domains.length === 0 && (
        <EmptyState
          title="No permission matches"
          hint="Search by part of a permission (certify, invoice) or a role (Physician), or clear the filters."
          action={<Button variant="secondary" size="sm" onClick={clear}>Clear filters</Button>}
        />
      )}

      <div className="space-y-4">
        {domains.map((domain) => (
          <section
            key={domain.domain}
            aria-labelledby={`perm-${domain.domain}`}
            className="overflow-hidden rounded-card border border-neutral-200 bg-white shadow-card"
          >
            <h2 id={`perm-${domain.domain}`} className="flex items-baseline gap-2 border-b border-neutral-100 px-4 pb-2.5 pt-3 text-body font-semibold text-neutral-900">
              {domainLabel(domain.domain)}
              <span className="text-caption font-normal tabular-nums text-neutral-500">{domain.permissions.length}</span>
            </h2>
            {/* Folded per area on a phone — the page was 13,000px — and opened
                by any filter, which is what finds a permission there. */}
            <PhoneFold
              label={`${domain.permissions.length} permission${domain.permissions.length === 1 ? '' : 's'}`}
              className="p-3"
              forceOpen={filtering}
            >
              <ul className="divide-y divide-neutral-100">
                {domain.permissions.map((p) => (
                  <li key={p.key} className="grid gap-x-6 gap-y-1.5 px-4 py-2.5 md:grid-cols-[minmax(14rem,20rem)_1fr]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-small font-medium text-neutral-900">{actionLabel(p.key)}</span>
                        <code className="break-all font-mono text-caption text-neutral-500">{p.key}</code>
                        {p.stepUp && (
                          <span title="Using this needs two-factor sign-in"
                                className="inline-flex items-center gap-1 rounded-badge bg-primary-50 px-1.5 py-0.5 text-caption text-primary-700">
                            <ShieldCheck className="h-3 w-3" aria-hidden /> Two-factor
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-wrap content-start items-center gap-1.5">
                      {p.heldBy.length === 0 ? (
                        // Worth surfacing rather than hiding: a permission no
                        // role holds gates an endpoint nobody can reach.
                        <span className="inline-flex items-center gap-1 rounded-badge bg-dueSoon-100 px-2 py-0.5 text-caption font-medium text-dueSoon-600">
                          <TriangleAlert className="h-3 w-3" aria-hidden /> Held by no role — nobody can use it
                        </span>
                      ) : (
                        p.heldBy.map((key) => (
                          <span
                            key={key}
                            title={key}
                            className={cn(
                              'rounded-badge border px-2 py-0.5 text-caption',
                              key === roleKey
                                ? 'border-primary-300 bg-primary-50 font-medium text-primary-700'
                                : 'border-neutral-200 bg-neutral-50 text-neutral-700',
                            )}
                          >
                            {roleName(key)}
                          </span>
                        ))
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </PhoneFold>
          </section>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, icon, tone, children }: {
  active: boolean; onClick: () => void; icon: ReactNode; tone?: 'warning'; children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-small transition-colors duration-hover max-md:min-h-10',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
        active
          ? 'border-primary-600 bg-primary-600 text-white'
          : tone === 'warning'
            ? 'border-dueSoon-600 bg-dueSoon-100 text-dueSoon-600 hover:brightness-95'
            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
      )}
    >
      {icon}{children}
    </button>
  );
}
